import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { isAdmin } from '@comps/commonFunctions';

/**
 * Ctrl/Cmd+K jump-to.
 *
 * Searches the permission-filtered nav tree already cached in
 * localStorage by NavSide, so it never shows a page the signed-in user
 * cannot open, and it needs no new endpoint. Typing digits also offers a
 * direct jump to a case by ID.
 */

const flattenNav = (items, trail = []) =>
  (items || []).reduce((acc, item) => {
    const path = [...trail, item.name];
    if (item.children && item.children.length > 0) {
      return acc.concat(flattenNav(item.children, path));
    }
    acc.push({
      key: item.route,
      name: item.name,
      icon: item.icon,
      trail: trail.join(' · '),
    });
    return acc;
  }, []);

const CommandSearch = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const pages = useMemo(() => {
    try {
      const nav = JSON.parse(localStorage.getItem('navigation') || '[]');
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
      // Same exemption NavSide's buildMenuItems makes, and for the same
      // reason: an admin is granted everything server side, so filtering
      // them against a permissions cache that is a sign-in behind left
      // Ctrl+K offering a shorter list of pages than the sidebar beside
      // it — and an empty one whenever that cache had not been written.
      const unrestricted = isAdmin();
      const allowed = (item) =>
        unrestricted || permissions.some((each) => each.key === item.key + '_index');

      // A group whose every child was pruned away is dropped rather
      // than kept as a leaf: its own route ("case-management") has no
      // component behind it, so offering it would hand the reader a
      // blank screen.
      const prune = (items) =>
        (items || []).reduce((kept, item) => {
          if (!allowed(item)) return kept;

          const wasGroup = item.children && item.children.length > 0;
          const children = wasGroup ? prune(item.children) : [];
          if (wasGroup && children.length === 0) return kept;

          kept.push({ ...item, children });
          return kept;
        }, []);

      return flattenNav(prune(nav));
    } catch (e) {
      return [];
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = q
      ? pages.filter((page) => page.name.toLowerCase().includes(q))
      : pages;

    const list = matched.slice(0, 8).map((page) => ({
      type: 'page',
      key: 'page-' + page.key,
      label: page.name,
      hint: page.trail,
      icon: page.icon || 'tabler:file',
      to: '/' + page.key,
    }));

    if (/^\d+$/.test(query.trim())) {
      list.unshift({
        type: 'case',
        key: 'case-' + query.trim(),
        label: `Open case ${query.trim()}`,
        hint: 'Case details',
        icon: 'tabler:folder-open',
        to: `/cases/details/${query.trim()}`,
      });
    }

    return list;
  }, [query, pages]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActive(0);
  }, []);

  const go = useCallback((item) => {
    if (!item) return;
    navigate(item.to);
    close();
  }, [navigate, close]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((wasOpen) => !wasOpen);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    setActive(0);
  }, [query]);

  const onFieldKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (results.length ? (i + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[active]);
    }
  };

  return (
    <>
      <button type="button" className="cmd-trigger" onClick={ () => setOpen(true) }>
        <Icon icon="tabler:search" width={ 18 } />
        <span className="cmd-trigger-label">Search pages</span>
        <span className="cmd-kbd">Ctrl K</span>
      </button>

      <Modal
        open={ open }
        onCancel={ close }
        footer={ null }
        closable={ false }
        destroyOnClose
        width={ 560 }
        className="cmd-modal"
        styles={ { body: { padding: 0 } } }
        afterOpenChange={ (isOpen) => isOpen && inputRef.current && inputRef.current.focus() }
      >
        <div className="cmd-field">
          <Icon icon="tabler:search" width={ 20 } />
          <input
            ref={ inputRef }
            value={ query }
            onChange={ (e) => setQuery(e.target.value) }
            onKeyDown={ onFieldKeyDown }
            placeholder="Search pages"
            aria-label="Search"
          />
        </div>

        <div className="cmd-results">
          { results.length === 0
            ? <div className="cmd-empty">Nothing matches “{ query }”. Try a page name.</div>
            : <>
              <div className="cmd-group-label">Jump to</div>
              { results.map((item, index) => (
                <button
                  key={ item.key }
                  type="button"
                  className={ `cmd-item ${index === active ? 'is-active' : ''}` }
                  onMouseEnter={ () => setActive(index) }
                  onClick={ () => go(item) }
                >
                  <Icon className="cmd-item-icon" icon={ item.icon } width={ 19 } />
                  <span>{ item.label }</span>
                  { item.hint && <span className="cmd-item-hint">{ item.hint }</span> }
                </button>
              )) }
            </> }
        </div>

        <div className="cmd-foot">
          <span><span className="cmd-kbd">↑↓</span> move</span>
          <span><span className="cmd-kbd">↵</span> open</span>
          <span><span className="cmd-kbd">esc</span> close</span>
        </div>
      </Modal>
    </>
  );
};

export default CommandSearch;
